import React from 'react';
import {
  isBoolean,
  isArray,
  isObject,
  isString,
  stripHtml,
  getItemId,
  areItemsEqual,
} from '../helpers';
import {
  isAttachment,
  isTranslation,
  isAccreditation,
  isExtra,
  isTheme,
  isSegment,
  isProgram,
} from '../typeCheckers';
import { getResolvedAttachment, getEnrichedValue } from '../labelGetters';
import EmptyValue from './EmptyValue';
import AttachmentDisplay from './AttachmentDisplay';
import TranslationDisplay from './TranslationDisplay';
import AccreditationDisplay from './AccreditationDisplay';
import ExtraDisplay from './ExtraDisplay';
import ThemeDisplay from './ThemeDisplay';
import SegmentDisplay from './SegmentDisplay';
import { ProgramDisplay } from './ProgramDisplays';
import ObjectValueDisplay from './ObjectValueDisplay';
import SmartItemDisplay from './SmartItemDisplay';

// History entries recorded before translations were diffed by locale carry
// every translation, not just the edited one. An untouched locale has no
// counterpart in the "after" column, so it only adds noise here.
function withoutUnchangedTranslations(items, compareItems) {
  if (!isArray(compareItems)) return items;

  const counterparts = new Map();
  for (const item of compareItems) {
    const id = getItemId(item);
    if (id) counterparts.set(id, item);
  }

  return items.filter((item) => {
    if (!isTranslation(item)) return true;
    const counterpart = counterparts.get(getItemId(item));
    return !counterpart || !areItemsEqual(counterpart, item);
  });
}

export default function ViewOnlyValue({
  value,
  compareValue,
  attachmentLabels,
  labels,
  fieldName,
  isBefore = true,
}) {
  // Check if value is empty
  if (value === null || value === undefined || value === '') {
    return <EmptyValue />;
  }

  if (isBoolean(value)) {
    return value ? (
      <i className="fa-regular fa-circle-check text-lg text-green-600 inline" />
    ) : (
      <i className="fa-regular fa-circle-xmark text-lg text-gray-400 inline" />
    );
  }

  if (isArray(value)) {
    const items = withoutUnchangedTranslations(value, compareValue);
    if (items.length === 0) {
      return <EmptyValue />;
    }
    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={getItemId(item) || index} className="text-sm text-gray-700">
            <SmartItemDisplay
              item={item}
              attachmentLabels={attachmentLabels}
              labels={labels}
              fieldName={fieldName}
            />
          </div>
        ))}
      </div>
    );
  }

  if (isObject(value)) {
    // Check for special types
    if (isAttachment(value)) {
      const resolvedAttachment = getResolvedAttachment(value, attachmentLabels);
      return (
        <AttachmentDisplay
          attachment={value}
          resolvedAttachment={resolvedAttachment}
        />
      );
    }
    if (isTranslation(value)) {
      return <TranslationDisplay translation={value} />;
    }
    if (isAccreditation(value, labels)) {
      return <AccreditationDisplay accreditation={value} labels={labels} />;
    }
    if (isExtra(value, labels)) {
      return <ExtraDisplay extra={value} labels={labels} />;
    }
    if (isTheme(value, labels)) {
      return <ThemeDisplay theme={value} labels={labels} />;
    }
    if (isSegment(value, labels)) {
      return <SegmentDisplay segment={value} labels={labels} />;
    }
    if (isProgram(value)) {
      return (
        <ProgramDisplay
          program={value}
          labels={labels}
          attachmentLabels={attachmentLabels}
        />
      );
    }
    return (
      <ObjectValueDisplay
        value={value}
        attachmentLabels={attachmentLabels}
        labels={labels}
      />
    );
  }

  if (isString(value)) {
    // For guiding languages, show uppercase
    if (fieldName === 'guidingLanguages') {
      return <span className="text-gray-700">{value.toUpperCase()}</span>;
    }
    const strippedValue = stripHtml(value);
    return (
      <span className="text-gray-700">{strippedValue || <EmptyValue />}</span>
    );
  }

  // For primitive values, try to get enriched display
  const enrichedValue = getEnrichedValue(value, fieldName, labels, isBefore);
  if (enrichedValue) {
    return <span className="text-gray-700">{enrichedValue}</span>;
  }

  return <span className="text-gray-700">{String(value)}</span>;
}
